import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, CalendarCheck, Clock, MapPin, ShieldCheck } from 'lucide-react';
import { Card, Button } from '../components';
import { getVpSlots, boekVp } from '../services/medewerkerApi';

type LocatieType = 'ONLINE' | 'WERKGEVER' | 'EXTERN';
const LOCATIE_TYPES: LocatieType[] = ['ONLINE', 'WERKGEVER', 'EXTERN'];

interface Slot {
  start_tijd: string;
  eind_tijd: string;
}

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

function dagKop(iso: string): string {
  return new Date(iso).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}

function tijd(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

/** Dossierloos gesprek plannen bij de vertrouwenspersoon (pijler 1, VP-variant). */
export const MedewerkerVpBoekingPage: React.FC = () => {
  const { meldingId } = useParams<{ meldingId: string }>();
  const { t } = useTranslation('medewerker');

  const [gekozen, setGekozen] = useState<Slot | null>(null);
  const [locatieType, setLocatieType] = useState<LocatieType>('ONLINE');
  const [bevestigd, setBevestigd] = useState<{ start_tijd: string; eind_tijd: string } | null>(null);

  const slotsQuery = useQuery({
    queryKey: ['medewerker-vp-slots', meldingId],
    queryFn: () => getVpSlots(meldingId!),
    enabled: Boolean(meldingId),
    retry: false,
  });

  const boekMutation = useMutation({
    mutationFn: () => boekVp(meldingId!, { start_tijd: gekozen!.start_tijd, locatie_type: locatieType }),
    onSuccess: (data) => setBevestigd({ start_tijd: data.start_tijd, eind_tijd: data.eind_tijd }),
  });

  const perDag = useMemo(() => {
    const groepen = new Map<string, Slot[]>();
    for (const slot of slotsQuery.data?.slots ?? []) {
      const dag = new Date(slot.start_tijd).toDateString();
      if (!groepen.has(dag)) groepen.set(dag, []);
      groepen.get(dag)!.push(slot);
    }
    return Array.from(groepen.entries());
  }, [slotsQuery.data]);

  const locatieLabels: Record<LocatieType, string> = {
    ONLINE: t('locatie.ONLINE', { defaultValue: 'Online (videobellen)' }),
    WERKGEVER: t('locatie.WERKGEVER', { defaultValue: 'Op het werk' }),
    EXTERN: t('locatie.EXTERN', { defaultValue: 'Externe locatie' }),
  };

  if (slotsQuery.isLoading) {
    return <div className="py-16 text-center text-gray-500">…</div>;
  }

  if (slotsQuery.isError || !slotsQuery.data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(slotsQuery.error, t('vp.laadfout', { defaultValue: 'De beschikbaarheid kon niet worden geladen.' }))}
        </p>
        <Link to="/medewerker/professionals" className="inline-block mt-4 text-sm text-[#0B1F3A] underline">
          {t('vp.terugNaarPool', { defaultValue: 'Terug naar Mijn Professionals' })}
        </Link>
      </Card>
    );
  }

  const info = slotsQuery.data;

  if (bevestigd) {
    return (
      <Card className="p-8 text-center">
        <CalendarCheck className="w-10 h-10 text-green-600 mx-auto mb-3" aria-hidden="true" />
        <h1 className="text-xl font-semibold text-gray-900">{t('vp.verzoekVerstuurd', { defaultValue: 'Je gespreksverzoek is verstuurd' })}</h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('vp.verzoekUitleg', { defaultValue: 'De vertrouwenspersoon bevestigt het tijdstip of stelt een ander moment voor. Je ziet het antwoord op je dashboard.' })}
        </p>
        <p className="text-gray-700 mt-2 capitalize">
          {dagKop(bevestigd.start_tijd)} · {tijd(bevestigd.start_tijd)}–{tijd(bevestigd.eind_tijd)}
        </p>
        <p className="text-sm text-gray-600 mt-1">{locatieLabels[locatieType]}</p>
        <p className="text-sm text-gray-600 mt-4">
          {t('vp.bevestigdUitleg', { defaultValue: 'Je werkgever ziet alleen een meldingscode — nooit met wie of waarover.' })}
        </p>
        <Link to="/medewerker" className="inline-block mt-6">
          <Button variant="outline">{t('vp.naarDashboard', { defaultValue: 'Naar mijn overzicht' })}</Button>
        </Link>
      </Card>
    );
  }

  const sessieLabel =
    info.sessie_type === 'intake'
      ? t('vp.intake', { defaultValue: 'Intakegesprek' })
      : t('vp.followup', { defaultValue: 'Vervolggesprek' });

  return (
    <div className="space-y-5">
      <Link to="/medewerker/professionals" className="inline-flex items-center gap-1 text-sm text-[#0B1F3A]">
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        {t('vp.terug', { defaultValue: 'Mijn Professionals' })}
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('vp.titel', { defaultValue: 'Plan je gesprek' })}</h1>
        {info.professional_naam && <p className="text-gray-700 mt-0.5">{info.professional_naam}</p>}
        <p className="text-sm text-purple-800 font-medium">{t('vp.rol', { defaultValue: 'Vertrouwenspersoon' })}</p>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <div>
            <p className="text-gray-500">{t('vp.soort', { defaultValue: 'Gesprek' })}</p>
            <p className="font-medium text-gray-900">
              {sessieLabel} · {t('vp.nummerVan', { defaultValue: '{{n}} van {{max}}', n: info.sessie_nummer, max: info.max_sessies })}
            </p>
          </div>
          <div>
            <p className="text-gray-500">{t('vp.duur', { defaultValue: 'Duur' })}</p>
            <p className="font-medium text-gray-900 flex items-center gap-1">
              <Clock className="w-4 h-4" aria-hidden="true" />
              {info.duur_minuten} min
            </p>
          </div>
        </div>
      </Card>

      <div className="rounded-lg bg-purple-50 border border-purple-100 p-3 flex items-start gap-2 text-sm text-gray-700">
        <ShieldCheck className="w-5 h-5 text-purple-700 shrink-0" aria-hidden="true" />
        <p>
          {t('vp.privacy', {
            defaultValue:
              'Dossierloos: je werkgever ziet alleen een meldingscode op de verzamelfactuur. Er worden geen verslagen gemaakt; chatgeschiedenis wordt na 24 uur gewist.',
          })}
        </p>
      </div>

      <Card className="p-4">
        <h2 className="font-semibold text-gray-900 mb-3">{t('vp.kiesMoment', { defaultValue: 'Kies een moment' })}</h2>
        {perDag.length === 0 ? (
          <p className="text-sm text-gray-500">{t('vp.geenSlots', { defaultValue: 'Nog geen beschikbare tijden. Probeer het later opnieuw.' })}</p>
        ) : (
          <div className="space-y-4">
            {perDag.map(([dag, slots]) => (
              <div key={dag}>
                <p className="text-sm font-medium text-gray-800 mb-2 capitalize">{dagKop(slots[0].start_tijd)}</p>
                <div className="flex flex-wrap gap-2">
                  {slots.map((slot) => {
                    const actief = gekozen?.start_tijd === slot.start_tijd;
                    return (
                      <button
                        key={slot.start_tijd}
                        type="button"
                        onClick={() => setGekozen(slot)}
                        aria-pressed={actief}
                        className={`px-3 py-2 rounded-lg border text-sm transition ${
                          actief ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:border-emerald-400'
                        }`}
                      >
                        {tijd(slot.start_tijd)}–{tijd(slot.eind_tijd)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <MapPin className="w-4 h-4" aria-hidden="true" />
          {t('vp.locatie', { defaultValue: 'Waar wil je afspreken?' })}
        </h2>
        <fieldset className="space-y-2">
          <legend className="sr-only">{t('vp.locatie', { defaultValue: 'Waar wil je afspreken?' })}</legend>
          {LOCATIE_TYPES.map((lt) => (
            <label
              key={lt}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm cursor-pointer ${
                locatieType === lt ? 'border-[#0B1F3A] bg-blue-50' : 'border-gray-300 bg-white'
              }`}
            >
              <input
                type="radio"
                name="locatie_type"
                value={lt}
                checked={locatieType === lt}
                onChange={() => setLocatieType(lt)}
                className="accent-[#0B1F3A]"
              />
              <span className="text-gray-900">{locatieLabels[lt]}</span>
            </label>
          ))}
        </fieldset>
      </Card>

      {boekMutation.isError && (
        <p className="text-sm text-red-600" role="alert">
          {foutMelding(boekMutation.error, t('vp.boekenMislukt', { defaultValue: 'Inplannen is niet gelukt. Kies een ander moment.' }))}
        </p>
      )}

      <Button size="lg" className="w-full" onClick={() => boekMutation.mutate()} disabled={!gekozen || boekMutation.isPending} loading={boekMutation.isPending}>
        {gekozen
          ? t('vp.bevestigMet', { defaultValue: 'Bevestig {{tijd}}', tijd: `${dagKop(gekozen.start_tijd)} ${tijd(gekozen.start_tijd)}` })
          : t('vp.bevestig', { defaultValue: 'Kies eerst een moment' })}
      </Button>
    </div>
  );
};

export default MedewerkerVpBoekingPage;
